import React, { useEffect, useMemo, useState } from "react";
import Header from "../../layout/header/Header";
import {
  Box,
  Button,
  Chip,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ApplicantMappingRow from "../new-booking-form/components/applicant-mapping/ApplicantMappingRow";
import { useFormik, setIn } from "formik";
import * as Yup from "yup";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getOppprtunityDetails,
  getVoucherApplicants,
  getTermsAndCondition,
  mapVoucherApplicants,
  setProjectBrandImage,
  setPartnerHeaderImage,
  setBrandLogo,
  setProjectId,
  setBrandId,
} from "../new-booking-form/new-booking-form.slice";
import Loader from "../../components/Loader";
import SnackbarAlert from "../../shared-components/snackbar-alert/SnackbarAlert";
import { getErrorMessage, termsApiErrorMessage } from "../../utilis/common-funtions";
import { BOOKING_AS } from "../../config/constant";

const applicantKeys = ["applicant1", "applicant2", "applicant3", "applicant4"];

function VoucherApplicantMapping() {
  const { t } = useTranslation();
  const { oppId } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  const {
    isLoading: bookingLoading,
    enquiryDetails,
    applicantData,
    voucherApplicantsData,
  } = useSelector((state) => state.newBookingForm);

  const { isLoading: voucherLoading } = useSelector(
    (state) => state.voucherForm || {}
  );

  const isLoading = bookingLoading || voucherLoading;

  const voucherId = voucherApplicantsData?.voucherNumberEOI;

  const formattedUnitPath = [
    enquiryDetails?.data?.ProjectName,
    enquiryDetails?.data?.BlockTower,
    enquiryDetails?.data?.UnitNo,
  ]
    .filter(Boolean)
    .join(" / ");

  /* ------------------- FIXED LENGTH CONDITION ------------------- */
const voucherApplicants = useMemo(() => {
  const applicants = voucherApplicantsData?.applicants;

  if (!applicants?.length) return [];

  return applicants.map((app) => ({
    value: app?.value,
    name: app?.name,
    isMinor: app?.isMinor,
  }));
}, [voucherApplicantsData]);


  /* ------------------- OPTIONS ------------------- */
  const applicantOptions = useMemo(() => {
    if (!applicantData?.bookingAs) return [];

    const options = [...voucherApplicants];

    options.push({
      value: "new_applicant",
      name:
        applicantData?.bookingAs === BOOKING_AS.INDIVIDUAL
          ? "New Applicant"
          : "New Authorized Signatory",
    });

    return options;
  }, [voucherApplicants, applicantData?.bookingAs]);

  /* ------------------- PROPER INITIAL VALUES ------------------- */
  const initialValues = useMemo(() => {
    return {
      mapping: {
        applicant1: voucherApplicants?.[0]?.value || "",
        applicant2: voucherApplicants?.[1]?.value || null,
        applicant3: voucherApplicants?.[2]?.value || null,
        applicant4: voucherApplicants?.[3]?.value || null,
      },
    };
  }, [voucherApplicants]);

  const [snackbarConfig, setSnackbarConfig] = useState({
    open: false,
    message: "",
    severity: "",
  });
  /** Terms must load before user can continue (images + journey gate). */
  const [termsJourneyOk, setTermsJourneyOk] = useState(false);
  const [termsErrorMessage, setTermsErrorMessage] = useState("");

  const formik = useFormik({
    initialValues,
    enableReinitialize: true, 
    validationSchema: Yup.object({
      mapping: Yup.object(
        applicantKeys.reduce((acc, key) => {
          acc[key] = Yup.string()
            .nullable()
            .when({
              is: (val) => val !== null,
              then: (schema) =>
                schema.required(
                  t("applicantMapping.validations.required", {
                    entity: applicantData?.bookingAs === BOOKING_AS.INDIVIDUAL
                      ? "Applicant"
                      : "Authorized signatory",
                  })
                ),
              otherwise: (schema) => schema.notRequired(),
            })
            .test(
              "unique",
              t("applicantMapping.validations.unique", {
                entity: applicantData?.bookingAs === BOOKING_AS.INDIVIDUAL
                  ? "Applicant"
                  : "Authorized signatory",
              }),
              function (value) {
                if (!value || value.trim() === "") return true;
                if (value === "new_applicant") return true;

                const otherValues = Object.entries(this.parent)
                  .filter(([k]) => k !== key)
                  .map(([_, v]) => v)
                  .filter(
                    (v) => v && v.trim() !== "" && v !== "new_applicant"
                  );

                return !otherValues.includes(value);
              }
            );
          return acc;
        }, {})
      ),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      if (!termsJourneyOk) {
        return;
      }
      setSubmitting(true);

      const payload = {
        voucherId: voucherApplicantsData?.voucherId,
        opportunityId: oppId,
        bookingAs: applicantData?.bookingAs,
        mapping: values.mapping,
      };

      try {
        const response = await dispatch(
          mapVoucherApplicants({ payload })
        ).unwrap();

        setSnackbarConfig({
          open: true,
          message:
            response?.message ||
            "Voucher applicants mapped successfully",
          severity: "success",
        });

        history.push(`/booking-form/${oppId}`);
      } catch (error) {
        setSnackbarConfig({
          open: true,
          message: getErrorMessage(error),
          severity: "error",
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  /* ------------------- FETCH DATA ------------------- */
  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setTermsJourneyOk(false);
      setTermsErrorMessage("");

      try {
        const res = await dispatch(getOppprtunityDetails(`/${oppId}`)).unwrap();
        if (cancelled) return;

        dispatch(getVoucherApplicants(`/${oppId}`));

        const projectName = res?.data?.ProjectName;
        if (!projectName) {
          const msg =
            "Project information is missing. Terms and conditions could not be loaded. You cannot continue.";
          setTermsErrorMessage(msg);
          setSnackbarConfig({
            open: true,
            message: msg,
            severity: "error",
          });
          return;
        }

        try {
          const termsData = await dispatch(
            getTermsAndCondition(`/${projectName}`)
          ).unwrap();
          if (cancelled) return;

          if (termsData) {
            if (termsData.projectId) dispatch(setProjectId(termsData.projectId));
            if (termsData.brandId) dispatch(setBrandId(termsData.brandId));
            dispatch(setProjectBrandImage(termsData.projectImage || ""));
            dispatch(setPartnerHeaderImage(termsData.jvPartnerLogo || ""));
            dispatch(setBrandLogo(termsData.brandLogo || ""));
            setTermsJourneyOk(true);
          } else {
            const msg =
              "Terms and conditions could not be loaded. You cannot continue.";
            setTermsErrorMessage(msg);
            dispatch(setProjectBrandImage(""));
            dispatch(setPartnerHeaderImage(""));
            dispatch(setBrandLogo(""));
            setSnackbarConfig({
              open: true,
              message: msg,
              severity: "error",
            });
          }
        } catch (err) {
          if (cancelled) return;
          dispatch(setProjectBrandImage(""));
          dispatch(setPartnerHeaderImage(""));
          dispatch(setBrandLogo(""));
          const msg = termsApiErrorMessage(err);
          setTermsErrorMessage(msg);
          setSnackbarConfig({
            open: true,
            message: msg,
            severity: "error",
          });
        }
      } catch (err) {
        if (cancelled) return;
        const msg = termsApiErrorMessage(err);
        setTermsErrorMessage(msg);
        setSnackbarConfig({
          open: true,
          message: msg,
          severity: "error",
        });
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [dispatch, oppId]);

  const handleSnackbarClose = () => {
    setSnackbarConfig({
      open: false,
      message: "",
      severity: "",
    });
  };

  const handleNext = async () => {
    if (!termsJourneyOk) {
      return;
    }
    const errors = await formik.validateForm();
    if (Object.keys(errors).length === 0) {
      formik.submitForm();
    } else {
      let touched = {};
      const markTouched = (errObj, path = "") => {
        Object.keys(errObj).forEach((key) => {
          const currentPath = path ? `${path}.${key}` : key;
          if (typeof errObj[key] === "object" && errObj[key] !== null) {
            markTouched(errObj[key], currentPath);
          } else {
            touched = setIn(touched, currentPath, true);
          }
        });
      };
      markTouched(errors);
      formik.setTouched(touched);
    }
  };

  const handleResetForm = () => {
    formik.resetForm();
  };

  const isIndividualFlow = applicantData?.bookingAs === BOOKING_AS.INDIVIDUAL;

  return (
    <>
      {isLoading && <Loader isLoading={isLoading} />}
      <Helmet>
        <title>{t("applicantMapping.helmet.title")}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Header />
      <Stack>
        <Grid
          container
          spacing={0}
          sx={{
            p: 0,
            paddingLeft: { xs: "16px", sm: "16px", md: "16px", lg: "7%" },
            paddingRight: { xs: "16px", sm: "16px", md: "16px", lg: "7%" },
            mb: { xs: 2, sm: 4, md: 5 },
          }}
        >
          <Grid
            item
            xs={12}
            sx={{
              textAlign: "center",
              pt: { xs: "16px", sm: "30px", md: "36px" },
              pb: { xs: "16px", sm: "18px", md: "20px" },
            }}
          >
            <Typography
              sx={{
                color: "#1A407D",
                fontSize: { xs: "20px", sm: "20px", md: "24px" },
                fontWeight: "700",
                mb: 1,
              }}
            >
              {t("applicantMapping.pageHeader.mainTitle")}
            </Typography>
            <Typography
              sx={{
                color: "#1C252E",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              {t("applicantMapping.pageHeader.subTitle")}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={{
                borderRadius: "8px",
                border: "1px solid rgba(26, 64, 125, 0.30)",
                padding: { xs: "16px", sm: "16px", lg: "26px 39px" },
              }}
            >
              <form
                onSubmit={(e) => {
                  if (!termsJourneyOk) {
                    e.preventDefault();
                    return;
                  }
                  formik.handleSubmit(e);
                }}
              >
                <Grid container spacing={3}>
                  <Grid
                    item
                    xs={12}
                    display="flex"
                    justifyContent="space-between"
                    gap={2}
                  >
                    <Typography
                      sx={{
                        color: "#1C252E",
                        fontSize: "16px",
                        fontWeight: "500",
                      }}
                    >
                      {`Dear ${voucherApplicants?.[0]?.name}, please select your ${applicantData?.bookingAs === BOOKING_AS.INDIVIDUAL ? "Applicant" : "Authorized signatory"} type for this booking`}
                    </Typography>

                    <Button
                      type="button"
                      sx={{
                        padding: "12px 20px",
                        borderRadius: "8px",
                        background: "#FFF",
                        color: "#1A407D",
                        border: "1px solid #D0D5DD",
                        fontSize: "16px",
                        fontWeight: "600",
                        lineHeight: "24px",
                        textTransform: "capitalize",
                        whiteSpace: "nowrap",
                        maxHeight: "50px",
                      }}
                      onClick={handleResetForm}
                      disabled={!termsJourneyOk}
                    >
                      {t("applicantMapping.buttons.reset")}
                    </Button>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sx={{
                      pt: {
                        xs: "24px !important",
                        sm: "0px !important",
                      },
                    }}
                  >
                    <Chip
                      label={`Voucher ID: ${voucherId}`}
                      sx={{
                        backgroundColor: "#C9DEFF",
                        color: "#1C252E",
                        fontWeight: 600,
                        fontSize: "16px",
                        py: "4px",
                        px: "10px",
                        borderRadius: "6px",
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <ApplicantMappingRow
                      formik={formik}
                      unitNo={formattedUnitPath}
                      applicantsPath="mapping"
                      applicantKeys={applicantKeys}
                      applicantOptions={applicantOptions}
                      isIndividualFlow={isIndividualFlow}
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    display="flex"
                    justifyContent="end"
                    gap={3}
                  >
                    <Button
                      endIcon={<NavigateNextIcon />}
                      type="button"
                      variant="contained"
                      sx={{
                        background: "#1A407D",
                        textTransform: "capitalize",
                        height: "48px",
                        width: "157px",
                      }}
                      onClick={handleNext}
                      disabled={formik.isSubmitting || !termsJourneyOk}
                    >
                      {t("applicantMapping.buttons.next")}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Box>
          </Grid>
        </Grid>
      </Stack>

      <SnackbarAlert
        open={
          snackbarConfig.open ||
          Boolean(!termsJourneyOk && termsErrorMessage)
        }
        message={snackbarConfig.message || termsErrorMessage}
        severity={
          !termsJourneyOk && termsErrorMessage
            ? "error"
            : snackbarConfig.severity
        }
        onClose={handleSnackbarClose}
        autoHideDuration={
          !termsJourneyOk && termsErrorMessage ? null : undefined
        }
      />
    </>
  );
}

export default VoucherApplicantMapping;
